import { getPages, getPageById } from '@studionand/ada-next/src/api/pages';
import { action, observable, computed } from 'mobx';
import { csvParse } from 'd3-dsv';
import { keyBy, uniqBy, find, orderBy, union } from 'lodash';
import {
  toNews,
  toMarketNews,
  toEvent,
  toSearchResults,
} from '../../utils/mapPages';
import {
  pagesWithTag,
  pagesWithDateBeforeToday,
  some,
} from '../../shared/utils/filter';
import {
  sortPageChronological,
  invert,
  sortByRanking,
} from '../../shared/utils/sort/';
import onlyDepiPages from '../onlyDepiPages';
import onlyDepvPages from '../onlyDepvPages';
import { handleResponse } from '../../api';

class Page {
  constructor(props) {
    this.tags = [];
    Object.keys(props).forEach(key => {
      this[key] = props[key];
    });
  }
}

class FilteredPages {
  constructor(filter, pages) {
    this.filter = filter;
    this.pages = pages;
  }

  asList() {
    return this.pages.asList.filter(this.filter);
  }
}

const FEATURE_TAGS = {
  pellets: ['Seite:Pellets', 'Startseite:Pellets'],
  holzbriketts: ['Seite:Briketts', 'Startseite:Briketts'],
  hackschnitzel: ['Seite:Hackschnitzel', 'Startseite:Hackschnitzel'],
};

class Pages {
  @observable
  isLoading = false;
  @observable
  _asList = [];
  @observable
  features = [];

  initialize = async () => {
    if (this.isLoaded) return;
    await this.fetchData();
  };

  hydrate = ({ _asList, features } = {}) => {
    if (_asList) this._asList = _asList;
    if (features) this.features = features;
  };

  @action.bound
  async fetchFeatures(context) {
    this.isLoading = true;
    const response = await getPages(9999, {
      attributes: [
        'title',
        'data',
        'uuid',
        'createdAt',
        'updatedAt',
        'tags',
        'type',
      ],
      tag: FEATURE_TAGS[context],
    });
    handleResponse([
      [
        200,
        () => {
          this.features = response.data.map(p => new Page(p));
        },
      ],
      [
        'default',
        () => {
          console.error(response);
        },
      ],
    ])(response);
    this.isLoading = false;
    return response;
  }

  @action.bound
  async fetchData() {
    try {
      this.isLoading = true;
      const response = await this.loadAllPages();
      handleResponse([
        [
          200,
          () => {
            this.receiveData(response);
          },
        ],
        [
          'default',
          () => {
            console.error(response);
          },
        ],
      ])(response);
      this.isLoading = false;
    } catch (e) {
      console.warn(e);
    }
  }

  @action.bound
  receiveData({ data }) {
    try {
      const parsed = csvParse(data);
      this._asList = parsed.map(c => new Page(c));
    } catch (e) {
      console.warn(e);
    }
  }

  @action.bound
  async loadNews() {
    this.isLoading = true;
    const response = await getPages(9999, {
      pageType: 'artikel',
      attributes: ['title', 'data', 'uuid', 'createdAt', 'tags', 'type'],
    });
    handleResponse([
      [
        200,
        () => {
          this.mergePages(response.data);
        },
      ],
      [
        'default',
        () => {
          console.error(response);
        },
      ],
    ])(response);
    this.isLoading = false;
    return response;
  }

  @action.bound
  async loadEvents() {
    this.isLoading = true;
    const response = await getPages(9999, {
      pageType: 'event',
      attributes: ['title', 'data', 'uuid', 'createdAt', 'tags', 'type'],
    });
    handleResponse([
      [
        200,
        () => {
          this.mergePages(response.data);
        },
      ],
      [
        'default',
        () => {
          console.error(response);
        },
      ],
    ])(response);
    this.isLoading = false;
    return response;
  }


  @action.bound
  async loadMarketData() {
    this.isLoading = true;
    const response = await getPages(9999, {
      pageType: 'datei',
      attributes: [
        'title',
        'data',
        'createdAt',
        'tags',
        'uuid',
        'updatedAt',
        'tags',
        'type',
      ],
    });
    handleResponse([
      [
        200,
        () => {
          this.mergePages(response.data);
        },
      ],
      [
        'default',
        () => {
          console.error(response);
        },
      ],
    ])(response);
    this.isLoading = false;
    return response;
  }

  @action.bound
  async loadAllPages(tag = []) {
    this.isLoading = true;
    const response = await getPages(9999, {
      attributes: [
        'title',
        'data',
        'createdAt',
        'tags',
        'uuid',
        'updatedAt',
        'tags',
        'type',
      ],
      tag,
    });
    handleResponse([
      [
        200,
        () => {
          this.mergePages(response.data);
        },
      ],
      [
        'default',
        () => {
          console.error(response);
        },
      ],
    ])(response);
    this.isLoading = false;
    // TODO: tags are not returned empty by default
    return response;
  }

  // async compare() {
  //   const responseA = await getPages(9999, {
  //     attributes: ['title', 'data', 'createdAt', 'tags', 'uuid', 'updatedAt', 'tags', 'type'],
  //   });
  //   const responseB = await getPages(9999, {
  //     pageType: 'artikel',
  //     attributes: ['title', 'data', 'uuid', 'createdAt', 'tags', 'type'],
  //   });

  //   const compA = responseA.data.filter(p => p.type === 'artikel').sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
  //   const compB = responseB.data.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));

  //   console.log(compA, compB)
  // }

  @action.bound
  async loadPage(uuid) {
    let page = null;
    const pageIndex = this._asList.findIndex(p => p.uuid === uuid);
    if (pageIndex > -1) {
      page = this._asList[pageIndex];
    } else {
      this.isLoading = true;
      const response = await getPageById(uuid);
      handleResponse([
        [
          200,
          () => {
            page = response.data;
            this.receivePage(page);
          },
        ],
        [
          'default',
          () => {
            console.error(response);
          },
        ],
      ])(response);
      this.isLoading = false;
    }
    return page;
  }

  @action.bound
  mergePages(pages) {
    const existingPages = this.asList.map(e => ({
      ...e,
      ...pages.find(p => p.uuid === e.uuid),
    }));

    const newPages = pages.filter(p => existingPages.findIndex(pp => pp.uuid === p.uuid) === -1);

    this._asList = [
      ...existingPages,
      ...newPages,
    ].map(p => new Page(p));
  }

  @action.bound
  receivePage(page) {
    const pageIndex = this._asList.findIndex(p => p.uuid === page.uuid);
    if (pageIndex > -1) {
      const existingPage = this._asList[pageIndex];
      this._asList[pageIndex] = new Page({
        ...existingPage,
        ...page,
      });
    } else {
      this._asList.push(new Page({ ...page }));
    }
  }

  @computed
  get news() {
    return this.asList.filter(p => p.type === 'artikel');
  }

  @computed
  get events() {
    return this.asList.filter(p => p.type === 'event');
  }

  @computed
  get isLoaded() {
    return this.asList.length > 0;
  }

  @computed
  get byId() {
    return keyBy(this.asList, 'uuid');
  }

  @computed
  get asList() {
    return this._asList;
  }

  @computed
  get depiPages() {
    return this.asList.filter(onlyDepiPages);
  }

  @computed
  get filterPages() {
    return this.asList.filter(onlyDepiPages);
  }

  @computed
  get latestEvents() {
    return this.events
      .map(toEvent)
      .filter(onlyDepiPages)
      .filter(pagesWithTag('Format:Termine'))
      .filter(
        pagesWithDateBeforeToday(p => p.endDate || p.startDate || undefined),
      )
      .sort(sortPageChronological)
      .slice(0, 3);
  }

  @computed
  get latestNews() {
    return this.news
      .map(toNews)
      .filter(onlyDepiPages)
      .filter(
        some([
          pagesWithTag('Format:News'),
          pagesWithTag('Format:Pressemitteilungen'),
        ]),
      )
      .sort(invert(sortPageChronological))
      .slice(0, 3);
  }

  /*
   * DEPV
   */

  @computed
  get depvPages() {
    return this.asList.filter(onlyDepvPages);
  }

  @computed
  get depvNews() {
    return this.depvPages.filter(p => p.type === 'artikel');
  }

  @computed
  get depvEvents() {
    return this.depvPages.filter(p => p.type === 'event');
  }

  @computed
  get depvPublications() {
    return this.depvPages.filter(p => p.type === 'datei');
  }

  @computed
  get latestMarketData() {
    return this.depvPublications
      .map(toMarketNews)
      .filter(p => p.type === 'datei')
      .filter(pagesWithTag('Format:Grafiken'))
      .filter(
        some([
          pagesWithTag('Thema:Pelletpreis'),
          pagesWithTag('Thema:Produktion'),
          pagesWithTag('Thema:Pelletkaminofen'),
          pagesWithTag('Thema:Pelletkessel'),
          pagesWithTag('Thema:Handel'),
        ]),
      )
      .sort(invert(sortPageChronological))
      .slice(0, 3);
  }

  @computed
  get latestDepvNews() {
    return this.depvNews
      .map(toNews)
      .filter(
        some([
          pagesWithTag('Format:News'),
          pagesWithTag('Format:Pressemitteilungen'),
        ]),
      )
      .sort(invert(sortPageChronological))
      .slice(0, 5);
  }

  @computed
  get latestDepvEvents() {
    return this.depvEvents
      .map(toEvent)
      .filter(pagesWithTag('Format:Termine'))
      .filter(
        pagesWithDateBeforeToday(p => p.endDate || p.startDate || undefined),
      )
      .sort(sortPageChronological)
      .slice(0, 3);
  }

  @computed
  get latestDepvPublications() {
    return this.depvPublications
      .map(toEvent)
      .filter(pagesWithTag('Format:Broschüren und Flyer'))
      .sort(invert(sortPageChronological))
      .slice(0, 3);
  }
}

export default Pages;
